import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { GridCellExpand, Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchUserTimeline } from '../../redux/modules/user/user.actions';
import { userTimelineSelector } from '../../redux/modules/user/user.selectors';
import { PaymentType, TimelineStatus } from '../../types/user';
import { round2Format, round4Format } from '../../utils/number';
import { TableCard } from '../../components/Section';

interface TimelineTabProps {
  userId: number;
}

const mapDetailsPropertyToFormatFn: Record<string, (v: number) => string> = {
  nav: round2Format,
  numOfUnits: round4Format,
  costBase: round2Format,
  leftUnits: round4Format,
  totalCostBase: round2Format,
  profit: round2Format,
  reconcile: round2Format,
};

function TimelineTab({ userId }: TimelineTabProps) {
  const columns = useRef<GridColDef[]>([
    { field: 'id', headerName: 'Id' },
    {
      field: 'productNameAr',
      headerName: 'Product Name (Ar)',
      width: 150,
      valueFormatter: (params) => params?.value,
    },
    {
      field: 'productNameEn',
      headerName: 'Product Name (En)',
      width: 150,
      valueFormatter: (params) => params?.value,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      valueFormatter: (params) => round2Format(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      valueFormatter: (params) => TimelineStatus[params?.value],
    },
    {
      field: 'isSubscription',
      headerName: 'Subscription',
      width: 150,
      valueFormatter: (params) => (params?.value ? 'Subscription' : 'Redemption'),
    },
    {
      field: 'paymentType',
      headerName: 'Payment Type',
      width: 150,
      valueFormatter: (params) => PaymentType[params?.value] || '-',
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      valueFormatter: (params) => params?.value,
    },
    {
      field: 'detail',
      headerName: 'Details',
      sortable: false,
      width: 350,
      valueFormatter: (params) => JSON.stringify(params.value, null, 2),
      renderCell: (params: GridRenderCellParams<IAnyObject>) => {
        const value = {
          ...(params.value || {}),
        };
        for (const [key, formatFn] of Object.entries(mapDetailsPropertyToFormatFn)) {
          if (value && value.hasOwnProperty(key)) {
            value[key] = formatFn(value[key]);
          }
        }

        return <GridCellExpand value={JSON.stringify(value, null, 2) || ''} width={params.colDef.computedWidth} />;
      },
    },
  ]);

  const { userTimeline, totalUserTimelineItems, fetchUserTimelineStatus } = useSelector(userTimelineSelector);
  const loading = fetchUserTimelineStatus === fetchStatuses.pending;

  return (
    <TableCard title="Timeline" description="Subscriptions and redemptions over time">
      <Table
        fetchItems={fetchUserTimeline}
        fetchItemsCustomParams={{ userId }}
        columns={columns?.current}
        rows={loading ? [] : userTimeline}
        loading={loading}
        rowCount={loading ? 0 : totalUserTimelineItems}
        disableColumnFilter
        disableColumnMenu
        initialColumnVisibilityModel={{ id: false }}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        toolbarExportProps={{
          csvOptions: { fileName: `user-${userId}-timeline-${Date.now()}` },
          excelOptions: { fileName: `user-${userId}-timeline-${Date.now()}` },
        }}
      />
    </TableCard>
  );
}

export default TimelineTab;
